import { useEffect, useRef, useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import { cn } from '../../../lib/cn';
import { Button } from '../../../components/ui/button';
import type { CloudPromptVersion } from '../types/cloud';

interface VersionDetailProps {
  version: CloudPromptVersion;
  onBack: () => void;
  onRestore: (versionId: string) => Promise<boolean>;
}

type RestoreStatus = 'idle' | 'restoring' | 'restored' | 'failed';

function formatVersionDate(createdAt: string): string {
  return new Date(createdAt).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export default function VersionDetail({
  version,
  onBack,
  onRestore,
}: Readonly<VersionDetailProps>): JSX.Element {
  const [restoreStatus, setRestoreStatus] = useState<RestoreStatus>('idle');
  const resetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (resetTimerRef.current) {
        clearTimeout(resetTimerRef.current);
      }
    };
  }, []);

  const handleRestore = async () => {
    setRestoreStatus('restoring');
    const success = await onRestore(version.id);
    setRestoreStatus(success ? 'restored' : 'failed');

    if (resetTimerRef.current) {
      clearTimeout(resetTimerRef.current);
    }

    resetTimerRef.current = setTimeout(() => {
      setRestoreStatus('idle');
      resetTimerRef.current = null;
    }, 2000);
  };

  const restoreLabel = (() => {
    switch (restoreStatus) {
      case 'restoring':
        return 'Restoring...';
      case 'restored':
        return 'Restored!';
      case 'failed':
        return 'Failed';
      default:
        return 'Make Active Version';
    }
  })();

  return (
    <div className="space-y-3">
      <Button type="button" variant="ghost" size="sm" onClick={onBack} className="w-fit px-2">
        <ArrowLeft className="h-4 w-4" />
        Back
      </Button>

      <div>
        <p className="m-0 mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">Title</p>
        <div className="rounded-md border border-border/70 bg-muted/20 p-3 text-sm text-foreground">
          {version.title}
        </div>
      </div>

      <div>
        <p className="m-0 mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">Content</p>
        <div className="max-h-[240px] overflow-y-auto whitespace-pre-wrap rounded-md border border-border/70 bg-muted/20 p-3 text-sm text-foreground">
          {version.content}
        </div>
      </div>

      <p className="m-0 text-xs text-muted-foreground">
        {(version.editedByName || version.editedBy)} - {formatVersionDate(version.createdAt)}
      </p>

      {!version.isCurrent ? (
        <Button
          type="button"
          onClick={handleRestore}
          disabled={restoreStatus === 'restoring'}
          className={cn(
            'w-full',
            restoreStatus === 'restored' && 'bg-emerald-600 hover:bg-emerald-700',
            restoreStatus === 'failed' && 'bg-destructive hover:bg-destructive/90',
          )}
        >
          {restoreLabel}
        </Button>
      ) : null}
    </div>
  );
}
